import { useQuery } from '@apollo/client';
import { GetLatestOversightRatingsByRiskIdsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery as useReactQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { isMoreRecentByTestDate } from '@/utils/oversightUtils';

export type OversightLeafRating = {
  Rating: number | null;
  Impact: number | null;
  Likelihood: number | null;
  ConfigId: string | null;
};

export type OversightRatingsByRisk = Map<
  string,
  { inherent?: OversightLeafRating; residual?: OversightLeafRating }
>;

const EMPTY_MAP: OversightRatingsByRisk = new Map();

type LeafWithTimestamps = {
  leaf: OversightLeafRating;
  TestDate: string | null;
  CreatedAt: string | null;
};

/**
 * Fetches the latest oversight inherent + residual rating per risk for the given
 * risk Ids, keyed by RiskId.
 *
 * "Latest" mirrors the risk page exactly
 * (getLatestOversightAssessmentRiskAssessmentResultsByRiskId): the oversight
 * result with the newest `TestDate` (tie-broken by `CreatedAtTimestamp`), both
 * read from the result row itself. Inherent (uncontrolled) and residual
 * (controlled) legs are tracked independently. There is no completion gate —
 * any oversight assessment result counts, so the rating surfaces as soon as it
 * is assigned.
 *
 * Skips the query when oversight is disabled or no risks are visible.
 */
export const useLatestOversightRatingsByRisks = (params: {
  riskIds: string[];
  isEnabled: boolean;
}): { ratingsByRisk: OversightRatingsByRisk; loading: boolean } => {
  const { riskIds, isEnabled } = params;
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const skip = !isEnabled || riskIds.length === 0;

  const { data, loading } = useQuery(
    GetLatestOversightRatingsByRiskIdsDocument,
    {
      skip: skip || trpcEnabled,
      variables: { RiskIds: riskIds },
      fetchPolicy: 'cache-and-network',
    }
  );

  const trpc = useTRPC();
  const { data: trpcData, isLoading: trpcLoading } = useReactQuery({
    ...trpc.frontend.oversight.ratingsByRiskIds.queryOptions({ riskIds }),
    enabled: trpcEnabled && !skip,
  });

  const ratingsByRisk = useMemo<OversightRatingsByRisk>(() => {
    if (trpcEnabled) {
      if (!trpcData) {
        return EMPTY_MAP;
      }

      const byRisk: OversightRatingsByRisk = new Map();
      for (const row of trpcData) {
        // Service returns null for an absent leg; the consumer map uses
        // optional keys, so drop nulls to undefined.
        byRisk.set(row.RiskId, {
          inherent: row.inherent ?? undefined,
          residual: row.residual ?? undefined,
        });
      }

      return byRisk;
    }

    if (!data) {
      return EMPTY_MAP;
    }

    const latestByRisk = new Map<
      string,
      {
        inherent?: LeafWithTimestamps;
        residual?: LeafWithTimestamps;
      }
    >();

    for (const row of data.risk_uncontrolled_oversight_result) {
      const candidate: LeafWithTimestamps = {
        leaf: {
          Rating: row.Rating ?? null,
          Impact: row.Impact ?? null,
          Likelihood: row.Likelihood ?? null,
          ConfigId: row.ConfigId ?? null,
        },
        TestDate: row.TestDate ?? null,
        CreatedAt: row.CreatedAtTimestamp ?? null,
      };
      // A finding can attach to multiple risks (insertChildRiskOversightResult
      // takes RiskIds[]). `riskParent` is server-filtered to ParentId ∈ $RiskIds,
      // so every entry maps to a risk we want to surface the rating against.
      for (const rp of row.riskParent) {
        const existing = latestByRisk.get(rp.ParentId) ?? {};
        if (
          !existing.inherent ||
          isMoreRecentByTestDate(existing.inherent, candidate)
        ) {
          existing.inherent = candidate;
        }
        latestByRisk.set(rp.ParentId, existing);
      }
    }

    for (const row of data.risk_controlled_oversight_result) {
      const candidate: LeafWithTimestamps = {
        leaf: {
          Rating: row.Rating ?? null,
          Impact: row.Impact ?? null,
          Likelihood: row.Likelihood ?? null,
          ConfigId: row.ConfigId ?? null,
        },
        TestDate: row.TestDate ?? null,
        CreatedAt: row.CreatedAtTimestamp ?? null,
      };
      for (const rp of row.riskParent) {
        const existing = latestByRisk.get(rp.ParentId) ?? {};
        if (
          !existing.residual ||
          isMoreRecentByTestDate(existing.residual, candidate)
        ) {
          existing.residual = candidate;
        }
        latestByRisk.set(rp.ParentId, existing);
      }
    }

    const result: OversightRatingsByRisk = new Map();
    for (const [id, entry] of latestByRisk) {
      result.set(id, {
        inherent: entry.inherent?.leaf,
        residual: entry.residual?.leaf,
      });
    }

    return result;
  }, [trpcEnabled, trpcData, data]);

  return { ratingsByRisk, loading: trpcEnabled ? trpcLoading : loading };
};
